import { useDashboardAsesores } from '@/hooks';
import { formatHorasDecimales, horasDeAudios, horasDeAudiosAprobados } from '@/utils/formatDuration';
import { normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { extractRechazos } from '@/utils/renderWithBold';
import { Download, ExternalLink, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ColumnPicker, type ColDef } from './ColumnPicker';

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(d?: string | Date | null) {
  if (!d) return '—';
  try {
    return new Date(d as string).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function pColor(v: number) {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

// Una asesoría EN_REVISION marcada autoAprobar=true ya está resuelta de cara al admin
// (preaprobación transparente) — debe verse y contarse exactamente igual que una Finalizada,
// nunca como "En revisión" (mismo criterio que la cola de revisión y el widget de resumen).
export function estadoParaMostrar(a: { estado: string; autoAprobar?: boolean }): string {
  // Delega en el helper central en vez de repetir la regla: esta copia había quedado desfasada
  // (no contemplaba SIN_EVALUAR + autoAprobar, que sí existe en datos reales).
  return normalizeAsesoriaEstado({ estado: a.estado, autoAprobar: a.autoAprobar });
}

// Mientras una asesoría está EN_CURSO, `horas` sigue en 0 hasta que el asesor la envía a
// revisión — se usa la duración de los audios como estimado en vivo. `null` solo cuando de
// verdad no hay ningún dato (ni horas ni audios), para seguir mostrando "—".
export function horasParaMostrar(a: { horas: number | string | null; audios?: Array<{ duracionSeg?: number }> }): number | null {
  const real = a.horas != null ? Number(a.horas) : 0;
  if (real > 0) return real;
  const estimado = horasDeAudios(a.audios);
  if (estimado > 0) return estimado;
  return a.horas != null ? real : null;
}

function estadoChip(estado: string) {
  const variants: Record<string, string> = {
    FINALIZADA: 'admin-chip text-[10px] bg-(--admin-green-bg) text-(--admin-green)',
    EN_CURSO: 'admin-chip text-[10px] bg-(--admin-blue-bg) text-(--admin-blue)',
    EN_REVISION: 'admin-chip text-[10px] bg-(--admin-amber-bg) text-(--admin-amber)',
    RECHAZADA: 'admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red)',
    REQUIERE_CORRECCIONES: 'admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red)',
    REVERTIDA: 'admin-chip text-[10px] bg-gray-100 text-gray-700 border-gray-300',
    SIN_EVALUAR: 'admin-chip text-[10px] bg-(--admin-purple-bg) text-(--admin-purple)',
  };
  const labels: Record<string, string> = {
    FINALIZADA: 'Finalizada',
    EN_CURSO: 'En curso',
    EN_REVISION: 'En revisión',
    RECHAZADA: 'Rechazada',
    REQUIERE_CORRECCIONES: 'Requiere correcciones',
    REVERTIDA: 'Revertida',
    SIN_EVALUAR: 'Sin evaluar',
  };
  return <span className={variants[estado] ?? 'admin-chip text-[10px]'}>{labels[estado] ?? estado}</span>;
}

// ── static column definitions ─────────────────────────────────────────────────

type StaticKey =
  | 'actividad'
  | 'objetivo'
  | 'asesor'
  | 'estado'
  | 'inicio'
  | 'fin'
  | 'horas'
  | 'acumuladoAprobado'
  | 'cumplimiento'
  | 'rechazos'
  | 'evidencias'
  | 'audios';

const STATIC_COLS: ColDef<StaticKey>[] = [
  { key: 'actividad', label: 'Actividad', defaultVisible: true },
  { key: 'objetivo', label: 'Objetivo', defaultVisible: true },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
  { key: 'inicio', label: 'Inicio', defaultVisible: true },
  { key: 'fin', label: 'Fin', defaultVisible: true },
  { key: 'horas', label: 'Horas', defaultVisible: true },
  { key: 'acumuladoAprobado', label: 'Acumulado Aprobado', defaultVisible: true },
  { key: 'cumplimiento', label: 'Cumplimiento', defaultVisible: true },
  { key: 'rechazos', label: 'Rechazos', defaultVisible: true },
  { key: 'evidencias', label: 'Evidencias', defaultVisible: false },
  { key: 'audios', label: 'Audios', defaultVisible: false },
];

// ── types ─────────────────────────────────────────────────────────────────────

interface NtcCumplimiento {
  ntcId: number;
  nombre: string;
  porcentaje: number;
  C?: number;
  CP?: number;
  NC?: number;
  NA?: number;
}

interface Asesoria {
  asesoriaId: string | number;
  orden?: number | null;
  objetivo: string;
  /** El historial trae { asesorId, nombre }; el dashboard de la empresa, solo el nombre. */
  asesor?: { asesorId: number; nombre: string } | string | null;
  /** Algunos endpoints (dashboard de la empresa) solo traen el id: el nombre se busca en la lista de asesores. */
  asesorId?: number | null;
  estado: string;
  autoAprobar?: boolean;
  fechaInicio: string | Date;
  fechaFin: string | Date | null;
  horas: number | string | null;
  cumplimientoGeneral?: number | null;
  evidencias?: { evidenciaId: number }[];
  audios?: { audioId: number; duracionSeg?: number; estado?: string }[];
  ntcCumplimiento?: NtcCumplimiento[];
  rechazos?: number;
  recomendaciones?: string | null;
  actaLink?: string | null;
}

interface Props {
  asesorias: Asesoria[];
  nitEmpresa: string;
  getAsesoriaUrl?: (nit: string, id: string | number) => string;
}

// ── bitmask helper definitions for columns query parameter ──────────────────
const BIT_MAP_KEYS = [
  'objetivo',
  'estado',
  'inicio',
  'fin',
  'horas',
  'cumplimiento',
  'rechazos',
  'evidencias',
  'audios',
  'norma1',
  'norma1_C',
  'norma1_CP',
  'norma1_NC',
  'norma1_NA',
  'norma2',
  'norma2_C',
  'norma2_CP',
  'norma2_NC',
  'norma2_NA',
  'norma3',
  'norma3_C',
  'norma3_CP',
  'norma3_NC',
  'norma3_NA',
  // Al final para no cambiar el significado de los códigos de columnas ya compartidos.
  'actividad',
  'asesor',
];

function keyToBitMapKey(key: string, ntcIds: number[]): string | null {
  if (!key.startsWith('ntc_')) {
    return key; // Static key
  }
  const parts = key.split('_');
  const ntcId = parseInt(parts[1], 10);
  const ntcIdx = ntcIds.indexOf(ntcId);
  if (ntcIdx === -1) return null;

  const alias = `norma${ntcIdx + 1}`;
  const sub = parts[2];
  return sub ? `${alias}_${sub}` : alias;
}

function bitMapKeyToKey(bitMapKey: string, ntcIds: number[]): string | null {
  const match = bitMapKey.match(/^norma(\d+)(?:_(C|CP|NC|NA))?$/);
  if (!match) {
    return bitMapKey; // Static key
  }
  const idx = parseInt(match[1], 10) - 1;
  const ntcId = ntcIds[idx];
  if (ntcId === undefined) return null;

  const sub = match[2];
  return sub ? `ntc_${ntcId}_${sub}` : `ntc_${ntcId}`;
}

function serializeToCode(visible: Record<string, boolean>, ntcIds: number[]): string {
  let mask = 0;
  BIT_MAP_KEYS.forEach((bitMapKey, bitPos) => {
    const key = bitMapKeyToKey(bitMapKey, ntcIds);
    if (key && visible[key]) {
      mask |= 1 << bitPos;
    }
  });
  return mask.toString(36);
}

function deserializeFromCode(code: string, ntcIds: number[]): Record<string, boolean> {
  const mask = parseInt(code, 36);
  const nextVisible = {} as Record<string, boolean>;
  if (isNaN(mask)) return nextVisible;

  BIT_MAP_KEYS.forEach((bitMapKey, bitPos) => {
    const key = bitMapKeyToKey(bitMapKey, ntcIds);
    if (key) {
      nextVisible[key] = (mask & (1 << bitPos)) !== 0;
    }
  });
  return nextVisible;
}

/** Nombre de la actividad según el orden de la asesoría en la empresa: Diagnóstico, Asesoría 1, 2, … */
function nombreActividad(orden?: number | null): string {
  if (orden == null) return '—';
  return orden === 1 ? 'Diagnóstico' : `Asesoría ${orden - 1}`;
}

// ── component ─────────────────────────────────────────────────────────────────

export function AsesoriaHistorialTable({ asesorias, nitEmpresa, getAsesoriaUrl }: Props) {
  const navigate = useNavigate();
  const { asesores } = useDashboardAsesores();
  const nombresAsesores = useMemo(() => new Map(asesores.map((x) => [String(x.asesorId), x.nombre])), [asesores]);
  const nombreAsesor = (a: Asesoria) =>
    (typeof a.asesor === 'string' ? a.asesor : a.asesor?.nombre) ||
    (a.asesorId != null ? nombresAsesores.get(String(a.asesorId)) : undefined) ||
    '—';

  // Derive NTC cols per NTC: one % column + C, CP, NC, NA sub-columns
  // All grouped by NTC name, all hidden by default
  const ntcCols = useMemo<ColDef<string>[]>(() => {
    const seen = new Map<number, string>(); // ntcId → nombre
    for (const a of asesorias) {
      for (const ntc of a.ntcCumplimiento ?? []) {
        if (!seen.has(ntc.ntcId)) seen.set(ntc.ntcId, ntc.nombre);
      }
    }
    const cols: ColDef<string>[] = [];
    for (const [ntcId, nombre] of seen) {
      cols.push({ key: `ntc_${ntcId}`, label: '% Cumpl.', defaultVisible: false, group: nombre });
      cols.push({ key: `ntc_${ntcId}_C`, label: 'C', defaultVisible: false, group: nombre });
      cols.push({ key: `ntc_${ntcId}_CP`, label: 'CP', defaultVisible: false, group: nombre });
      cols.push({ key: `ntc_${ntcId}_NC`, label: 'NC', defaultVisible: false, group: nombre });
      cols.push({ key: `ntc_${ntcId}_NA`, label: 'NA', defaultVisible: false, group: nombre });
    }
    return cols;
  }, [asesorias]);

  const allCols: ColDef<string>[] = useMemo(() => [...(STATIC_COLS as ColDef<string>[]), ...ntcCols], [ntcCols]);

  // Derive unique NTC IDs in order they appear in the data
  const ntcIds = useMemo(() => {
    const ids = new Set<number>();
    for (const a of asesorias) {
      for (const ntc of a.ntcCumplimiento ?? []) {
        ids.add(ntc.ntcId);
      }
    }
    return Array.from(ids);
  }, [asesorias]);

  const defaultVisibleMap = useMemo(() => {
    const def = {} as Record<string, boolean>;
    allCols.forEach((c) => {
      def[c.key] = c.defaultVisible ?? true;
    });
    return def;
  }, [allCols]);

  // Toggle: si está activo (default), las horas de audios ya aprobados dentro de una asesoría
  // rechazada se propagan al "Acumulado Aprobado" de las filas siguientes. Si se apaga, la
  // columna vuelve al criterio estricto (solo asesorías Finalizadas/preaprobadas cuentan).
  const [incluirAudiosAprobadosCorreccion, setIncluirAudiosAprobadosCorreccion] = useState(true);

  const [visible, setVisible] = useState<Record<string, boolean>>(() => {
    const storedCode = localStorage.getItem('tourixia:showColumns');
    if (!storedCode) {
      return defaultVisibleMap;
    }

    const nextVisible = deserializeFromCode(storedCode, ntcIds);
    const result = {} as Record<string, boolean>;
    allCols.forEach((c) => {
      result[c.key] = nextVisible[c.key] ?? false;
    });

    if (Object.values(result).some(Boolean)) {
      return result;
    }
    return defaultVisibleMap;
  });

  // Re-inicializar cuando cambia la empresa o las normas asociadas
  useEffect(() => {
    const storedCode = localStorage.getItem('tourixia:showColumns');
    if (!storedCode) {
      setVisible(defaultVisibleMap);
      return;
    }

    const nextVisible = deserializeFromCode(storedCode, ntcIds);
    const result = {} as Record<string, boolean>;
    allCols.forEach((c) => {
      result[c.key] = nextVisible[c.key] ?? false;
    });

    if (Object.values(result).some(Boolean)) {
      setVisible(result);
    } else {
      setVisible(defaultVisibleMap);
    }
  }, [nitEmpresa, ntcIds, defaultVisibleMap, allCols]);

  const updateVisible = (nextVisible: Record<string, boolean>) => {
    setVisible(nextVisible);
    const isDefault = allCols.every((c) => !!nextVisible[c.key] === (c.defaultVisible ?? true));
    if (isDefault) {
      localStorage.removeItem('tourixia:showColumns');
    } else {
      const code = serializeToCode(nextVisible, ntcIds);
      localStorage.setItem('tourixia:showColumns', code);
    }
  };

  const toggle = (key: string) => {
    const nextVisible = { ...visible, [key]: !visible[key] };
    if (!Object.values(nextVisible).some(Boolean)) return;
    updateVisible(nextVisible);
  };

  const reset = () => {
    setVisible(defaultVisibleMap);
    localStorage.removeItem('tourixia:showColumns');
  };

  const selectAll = () => {
    const nextVisible = {} as Record<string, boolean>;
    allCols.forEach((c) => {
      nextVisible[c.key] = true;
    });
    updateVisible(nextVisible);
  };

  const toggleGroup = (groupCols: ColDef<string>[]) => {
    const allGroupVisible = groupCols.every((c) => visible[c.key]);
    const nextVisible = { ...visible };
    for (const c of groupCols) {
      nextVisible[c.key] = !allGroupVisible;
    }
    if (!Object.values(nextVisible).some(Boolean)) return;
    updateVisible(nextVisible);
  };

  const activeCount = Object.values(visible).filter(Boolean).length;

  const activeCols = allCols.filter((c) => visible[c.key]);

  // Reverse chronological order by the asesoria's `orden` (newest first: latest asesoría at the top,
  // diagnóstico last), regardless of the order the backend returns them in (it sorts by fechaFin).
  // Rows without `orden` fall to the end, tie-broken by asesoriaId to keep a stable sequence.
  const asesoriasOrdenadas = useMemo(() => {
    return [...asesorias].sort((a, b) => {
      const oa = a.orden ?? Number.MIN_SAFE_INTEGER;
      const ob = b.orden ?? Number.MIN_SAFE_INTEGER;
      if (oa !== ob) return ob - oa;
      return Number(b.asesoriaId) - Number(a.asesoriaId);
    });
  }, [asesorias]);

  // Acumulado de horas aprobadas hasta cada asesoría, en orden cronológico (más vieja primero).
  // Suman las que ya están "Aprobadas" de cara al admin (FINALIZADA real o EN_REVISION
  // preaprobada — mismo criterio que estadoParaMostrar) MÁS, para una fila que no lo está, la
  // duración de sus audios ya aprobados individualmente (revisión granular) — esas horas están
  // igual de confirmadas y deben propagarse al acumulado de las filas siguientes, aunque la
  // asesoría en general siga con correcciones pendientes por otro motivo. La fila propia sigue
  // mostrando "—" (con el check de audios aprobados aparte) porque ELLA no está aprobada — pero
  // el acumulado que carga hacia adelante sí crece.
  const acumuladoAprobadoPorId = useMemo(() => {
    const map = new Map<string | number, number>();
    const cronologico = [...asesorias].sort((a, b) => {
      const oa = a.orden ?? Number.MAX_SAFE_INTEGER;
      const ob = b.orden ?? Number.MAX_SAFE_INTEGER;
      if (oa !== ob) return oa - ob;
      return Number(a.asesoriaId) - Number(b.asesoriaId);
    });
    let acumulado = 0;
    for (const a of cronologico) {
      if (estadoParaMostrar(a) === 'FINALIZADA') {
        acumulado += horasParaMostrar(a) ?? 0;
      } else if (incluirAudiosAprobadosCorreccion) {
        acumulado += horasDeAudiosAprobados(a.audios);
      }
      map.set(a.asesoriaId, acumulado);
    }
    return map;
  }, [asesorias, incluirAudiosAprobadosCorreccion]);

  // Total agregado de horas ya aprobadas a nivel de audio dentro de asesorías que en general
  // siguen con correcciones — badge junto a "Exportar Tabla" para verlo sin scrollear la tabla.
  const totalAprobadoEnRechazadas = useMemo(() => {
    return asesorias.reduce((sum, a) => sum + (estadoParaMostrar(a) === 'FINALIZADA' ? 0 : horasDeAudiosAprobados(a.audios)), 0);
  }, [asesorias]);

  const exportToExcel = () => {
    // Helper to get Excel column name from 0-based index
    const getExcelColumnName = (colIndex: number): string => {
      let colName = '';
      let temp = colIndex;
      while (temp >= 0) {
        colName = String.fromCharCode((temp % 26) + 65) + colName;
        temp = Math.floor(temp / 26) - 1;
      }
      return colName;
    };

    // Custom widths map
    const CUSTOM_WIDTHS: Record<string, number> = {
      actividad: 15,
      objetivo: 50,
      asesor: 25,
      estado: 15,
      inicio: 15,
      fin: 15,
      horas: 12,
      acumuladoAprobado: 15,
      cumplimiento: 15,
      rechazos: 10,
      evidencias: 12,
      audios: 12,
    };

    // Filter dynamic norms that have at least one visible sub-column
    const ntcIdsVisibles = ntcIds.filter((ntcId) => {
      return (
        visible[`ntc_${ntcId}`] ||
        visible[`ntc_${ntcId}_C`] ||
        visible[`ntc_${ntcId}_CP`] ||
        visible[`ntc_${ntcId}_NC`] ||
        visible[`ntc_${ntcId}_NA`]
      );
    });

    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const tr1 = document.createElement('tr');

    // El rowspan de los headers estáticos solo debe ser 2 cuando de verdad va a existir una
    // segunda fila de sub-headers (columnas de norma visibles). Si no hay ninguna norma visible,
    // tr2 nunca se crea (ver más abajo) — dejar rowspan=2 igual desincroniza table_to_sheet
    // (SheetJS reserva una fila "fantasma" para el rowspan que nunca llega) y termina comiéndose
    // la primera fila real de datos del tbody.
    const headerRowspan = ntcIdsVisibles.length > 0 ? '2' : '1';

    // Static columns visible
    const staticColsVisibles = STATIC_COLS.filter((c) => visible[c.key]);
    staticColsVisibles.forEach((c) => {
      const th = document.createElement('th');
      th.setAttribute('rowspan', headerRowspan);
      th.textContent = c.label;
      tr1.appendChild(th);
    });

    // Dynamic NTC columns visible with forced 5-column width (colspan=5)
    ntcIdsVisibles.forEach((ntcId) => {
      const ntcName =
        asesorias.find((a) => a.ntcCumplimiento?.some((n) => n.ntcId === ntcId))?.ntcCumplimiento?.find((n) => n.ntcId === ntcId)?.nombre ||
        `Norma ${ntcId}`;
      const th = document.createElement('th');
      th.setAttribute('colspan', '5');
      th.textContent = ntcName;
      tr1.appendChild(th);
    });
    thead.appendChild(tr1);

    // Row 2 of headers: always append C, CP, NC, NA and % Cumpl. for each visible norm
    if (ntcIdsVisibles.length > 0) {
      const tr2 = document.createElement('tr');
      ntcIdsVisibles.forEach(() => {
        ['% Cumpl.', 'C', 'CP', 'NC', 'NA'].forEach((subLabel) => {
          const th = document.createElement('th');
          th.textContent = subLabel;
          tr2.appendChild(th);
        });
      });
      thead.appendChild(tr2);
    }
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    asesoriasOrdenadas.forEach((a) => {
      const tr = document.createElement('tr');

      if (visible.actividad) {
        const td = document.createElement('td');
        td.textContent = nombreActividad(a.orden);
        tr.appendChild(td);
      }
      if (visible.objetivo) {
        const td = document.createElement('td');
        td.textContent = a.objetivo;
        tr.appendChild(td);
      }
      if (visible.asesor) {
        const td = document.createElement('td');
        td.textContent = nombreAsesor(a);
        tr.appendChild(td);
      }
      if (visible.estado) {
        const td = document.createElement('td');
        td.textContent = estadoParaMostrar(a);
        tr.appendChild(td);
      }
      if (visible.inicio) {
        const td = document.createElement('td');
        td.textContent = formatDate(a.fechaInicio);
        tr.appendChild(td);
      }
      if (visible.fin) {
        const td = document.createElement('td');
        td.textContent = formatDate(a.fechaFin);
        tr.appendChild(td);
      }
      if (visible.horas) {
        const td = document.createElement('td');
        const horasVal = horasParaMostrar(a);
        td.textContent = horasVal != null ? formatHours(horasVal) : '—';
        tr.appendChild(td);
      }
      if (visible.acumuladoAprobado) {
        const td = document.createElement('td');
        if (estadoParaMostrar(a) === 'FINALIZADA') {
          td.textContent = formatHours(acumuladoAprobadoPorId.get(a.asesoriaId) ?? 0);
        } else {
          const horasAudiosAprobados = incluirAudiosAprobadosCorreccion ? horasDeAudiosAprobados(a.audios) : 0;
          td.textContent = horasAudiosAprobados > 0 ? `— (✓ ${formatHorasDecimales(horasAudiosAprobados)} en audios)` : '—';
        }
        tr.appendChild(td);
      }
      if (visible.cumplimiento) {
        const td = document.createElement('td');
        if (a.cumplimientoGeneral != null) {
          td.setAttribute('data-t', 'n');
          td.setAttribute('data-z', '0.0%');
          td.textContent = String(a.cumplimientoGeneral / 100);
        } else {
          td.textContent = '—';
        }
        tr.appendChild(td);
      }
      if (visible.rechazos) {
        const td = document.createElement('td');
        td.textContent = String(a.rechazos ?? extractRechazos(a.recomendaciones ?? '').length);
        tr.appendChild(td);
      }
      if (visible.evidencias) {
        const td = document.createElement('td');
        td.textContent = String(a.evidencias?.length ?? 0);
        tr.appendChild(td);
      }
      if (visible.audios) {
        const td = document.createElement('td');
        td.textContent = String(a.audios?.length ?? 0);
        tr.appendChild(td);
      }

      const ntcMap = new Map<string, number | undefined>();
      for (const ntc of a.ntcCumplimiento ?? []) {
        ntcMap.set(`ntc_${ntc.ntcId}`, ntc.porcentaje);
        ntcMap.set(`ntc_${ntc.ntcId}_C`, ntc.C);
        ntcMap.set(`ntc_${ntc.ntcId}_CP`, ntc.CP);
        ntcMap.set(`ntc_${ntc.ntcId}_NC`, ntc.NC);
        ntcMap.set(`ntc_${ntc.ntcId}_NA`, ntc.NA);
      }

      // Always render all 5 sub-columns for each visible norm
      ntcIdsVisibles.forEach((ntcId) => {
        const p = ntcMap.get(`ntc_${ntcId}`);
        const c = ntcMap.get(`ntc_${ntcId}_C`);
        const cp = ntcMap.get(`ntc_${ntcId}_CP`);
        const nc = ntcMap.get(`ntc_${ntcId}_NC`);
        const na = ntcMap.get(`ntc_${ntcId}_NA`);

        // % Cumpl (placeholder value, will be overwritten by formula)
        const tdP = document.createElement('td');
        tdP.setAttribute('data-t', 'n');
        tdP.setAttribute('data-z', '0.0%');
        tdP.textContent = p !== undefined ? String(p / 100) : '0';
        tr.appendChild(tdP);

        // C
        const tdC = document.createElement('td');
        tdC.setAttribute('data-t', 'n');
        tdC.textContent = c !== undefined ? String(c) : '0';
        tr.appendChild(tdC);

        // CP
        const tdCP = document.createElement('td');
        tdCP.setAttribute('data-t', 'n');
        tdCP.textContent = cp !== undefined ? String(cp) : '0';
        tr.appendChild(tdCP);

        // NC
        const tdNC = document.createElement('td');
        tdNC.setAttribute('data-t', 'n');
        tdNC.textContent = nc !== undefined ? String(nc) : '0';
        tr.appendChild(tdNC);

        // NA
        const tdNA = document.createElement('td');
        tdNA.setAttribute('data-t', 'n');
        tdNA.textContent = na !== undefined ? String(na) : '0';
        tr.appendChild(tdNA);
      });

      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    const ws = XLSX.utils.table_to_sheet(table);

    // Write Excel formulas for % Cumpl dynamic columns in each row (starting at Excel row 3)
    const staticCount = staticColsVisibles.length;
    const cumplimientoColIdx = staticColsVisibles.findIndex((c) => c.key === 'cumplimiento');

    asesoriasOrdenadas.forEach((_, index) => {
      const R = index + 3; // Excel row number (1-based, after headers)

      // Calculate individual NTC compliance formulas
      ntcIdsVisibles.forEach((_, ntcIndex) => {
        const colIdxPercent = staticCount + ntcIndex * 5;
        const colIdxC = colIdxPercent + 1;
        const colIdxCP = colIdxPercent + 2;
        const colIdxNC = colIdxPercent + 3;

        const colPName = getExcelColumnName(colIdxPercent);
        const colCName = getExcelColumnName(colIdxC);
        const colCPName = getExcelColumnName(colIdxCP);
        const colNCName = getExcelColumnName(colIdxNC);

        const cellRef = `${colPName}${R}`;
        ws[cellRef] = {
          t: 'n',
          f: `IF((${colCName}${R}+${colCPName}${R}+${colNCName}${R})=0,0,(${colCName}${R}+${colCPName}${R}/2)/(${colCName}${R}+${colCPName}${R}+${colNCName}${R}))`,
          z: '0.0%',
        };
      });

      // If exactly 3 norms are visible, compute global compliance as their AVERAGE formula
      if (visible.cumplimiento && cumplimientoColIdx !== -1 && ntcIdsVisibles.length === 3) {
        const colGeneralName = getExcelColumnName(cumplimientoColIdx);
        const cellsToAverage = ntcIdsVisibles.map((_, ntcIndex) => {
          const colIdxPercent = staticCount + ntcIndex * 5;
          return `${getExcelColumnName(colIdxPercent)}${R}`;
        });

        ws[`${colGeneralName}${R}`] = {
          t: 'n',
          f: `AVERAGE(${cellsToAverage.join(',')})`,
          z: '0.0%',
        };
      }
    });

    // Apply custom widths and fallback to auto-fit
    const colsWidths: { wch: number }[] = [];
    Object.keys(ws)
      .filter((k) => !k.startsWith('!'))
      .forEach((cellKey) => {
        const colIndex = XLSX.utils.decode_cell(cellKey).c;
        const cellValue = ws[cellKey].v ? String(ws[cellKey].v) : '';

        let colKey = '';
        if (colIndex < staticColsVisibles.length) {
          colKey = staticColsVisibles[colIndex].key;
        }

        const customW = CUSTOM_WIDTHS[colKey];
        const calculatedW = cellValue.length + 3;
        const finalW = customW !== undefined ? customW : Math.max(colsWidths[colIndex]?.wch || 8, calculatedW);

        colsWidths[colIndex] = { wch: finalW };
      });
    ws['!cols'] = colsWidths;

    // Apply cell formatting styling objects (for readers that support styles)
    Object.keys(ws)
      .filter((k) => !k.startsWith('!'))
      .forEach((key) => {
        const cell = ws[key];
        cell.s = cell.s || {};

        // Thin borders for the entire table grid
        cell.s.border = {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } },
        };

        // Bold titles and headers filling (rows 1 and 2)
        const rowNum = parseInt(key.replace(/^[A-Z]+/, ''), 10);
        if (rowNum <= 2) {
          cell.s.font = { bold: true };
          cell.s.fill = { fgColor: { rgb: 'F3F4F6' } };
        }
      });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial');
    XLSX.writeFile(wb, `Historial_Asesorias_${nitEmpresa}.xlsx`);
  };

  return (
    <div className="admin-card">
      {/* Header */}
      <div className="admin-card-header flex items-center justify-between gap-3">
        <div className="text-[13px] font-semibold">
          Historial de asesorías <span className="text-(--admin-text3) font-normal">({asesorias.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {totalAprobadoEnRechazadas > 0 && (
            <button
              type="button"
              role="switch"
              aria-checked={incluirAudiosAprobadosCorreccion}
              onClick={() => setIncluirAudiosAprobadosCorreccion((v) => !v)}
              className="flex items-center gap-2 px-2 py-1 text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded cursor-pointer hover:bg-amber-100 transition-colors"
              title={
                incluirAudiosAprobadosCorreccion
                  ? 'Incluyendo en el Acumulado Aprobado las horas de audios ya aprobados dentro de asesorías con correcciones — clic para excluirlas'
                  : 'Excluidas del Acumulado Aprobado — clic para incluir las horas de audios ya aprobados dentro de asesorías con correcciones'
              }
            >
              <span
                className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                  incluirAudiosAprobadosCorreccion ? 'bg-amber-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    incluirAudiosAprobadosCorreccion ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </span>
              <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
              {formatHorasDecimales(totalAprobadoEnRechazadas)} en corrección
            </button>
          )}
          <button
            onClick={exportToExcel}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded transition-colors cursor-pointer"
            title="Exportar a Excel"
          >
            <Download className="w-3.5 h-3.5 text-gray-500" />
            <span>Exportar Tabla</span>
          </button>
          <ColumnPicker
            cols={allCols}
            visible={visible as Record<string, boolean>}
            onToggle={toggle}
            onReset={reset}
            onSelectAll={selectAll}
            onToggleGroup={toggleGroup as (groupCols: ColDef<string>[]) => void}
            activeCount={activeCount}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              {activeCols.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {asesorias.length === 0 && (
              <tr>
                <td colSpan={activeCols.length + 1} className="text-center py-8 text-(--admin-text3)">
                  Sin asesorías registradas
                </td>
              </tr>
            )}
            {asesoriasOrdenadas.map((a) => {
              // Build lookup: key → value for each NTC column
              const ntcMap = new Map<string, number | undefined>();
              for (const ntc of a.ntcCumplimiento ?? []) {
                ntcMap.set(`ntc_${ntc.ntcId}`, ntc.porcentaje);
                ntcMap.set(`ntc_${ntc.ntcId}_C`, ntc.C);
                ntcMap.set(`ntc_${ntc.ntcId}_CP`, ntc.CP);
                ntcMap.set(`ntc_${ntc.ntcId}_NC`, ntc.NC);
                ntcMap.set(`ntc_${ntc.ntcId}_NA`, ntc.NA);
              }
              const rechazos = a.rechazos ?? extractRechazos(a.recomendaciones ?? '').length;

              return (
                <tr
                  key={a.asesoriaId}
                  className="clickable cursor-pointer hover:bg-(--admin-surface2) group"
                  title={a.actaLink ? 'Ctrl+clic (Cmd+clic en Mac) para abrir el acta' : undefined}
                  onClick={(e) => {
                    // Ctrl/Cmd+clic abre el acta en una pestaña nueva en vez de navegar al
                    // detalle — solo si esta asesoría tiene acta generada.
                    if ((e.ctrlKey || e.metaKey) && a.actaLink) {
                      window.open(a.actaLink, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    const url = getAsesoriaUrl
                      ? getAsesoriaUrl(nitEmpresa, a.asesoriaId)
                      : `/empresas/${nitEmpresa}/asesorias/${a.asesoriaId}`;
                    if (e.shiftKey) {
                      window.open(url, '_blank');
                    } else {
                      navigate(url);
                    }
                  }}
                >
                  {visible.actividad && <td className="whitespace-nowrap text-(--admin-text2)">{nombreActividad(a.orden)}</td>}
                  {visible.objetivo && <td className="font-medium text-(--admin-text) max-w-72 truncate">{a.objetivo}</td>}
                  {visible.asesor && <td className="text-(--admin-text2) whitespace-nowrap">{nombreAsesor(a)}</td>}
                  {visible.estado && <td>{estadoChip(estadoParaMostrar(a))}</td>}
                  {visible.inicio && <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(a.fechaInicio)}</td>}
                  {visible.fin && <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(a.fechaFin)}</td>}
                  {visible.horas && (
                    <td className="text-(--admin-text2) whitespace-nowrap">
                      {(() => {
                        const horasVal = horasParaMostrar(a);
                        return horasVal != null ? formatHours(horasVal) : '—';
                      })()}
                    </td>
                  )}
                  {visible.acumuladoAprobado && (
                    <td className="text-(--admin-text2) whitespace-nowrap">
                      {(() => {
                        if (estadoParaMostrar(a) === 'FINALIZADA') {
                          return formatHours(acumuladoAprobadoPorId.get(a.asesoriaId) ?? 0);
                        }
                        const horasAudiosAprobados = incluirAudiosAprobadosCorreccion ? horasDeAudiosAprobados(a.audios) : 0;
                        if (horasAudiosAprobados <= 0) return '—';
                        return (
                          <span
                            className="inline-flex items-center gap-1"
                            title={`${formatHorasDecimales(horasAudiosAprobados)} ya aprobadas a nivel de audio, aunque la asesoría requiera correcciones por otro motivo`}
                          >
                            <span>—</span>
                            <ShieldCheck className="w-3 h-3 text-amber-600" />
                            <span className="text-amber-700 text-[11px] font-semibold">{formatHorasDecimales(horasAudiosAprobados)}</span>
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {visible.cumplimiento && (
                    <td className="text-center whitespace-nowrap">
                      {a.cumplimientoGeneral != null ? (
                        <span className="font-bold text-[12px]" style={{ color: pColor(a.cumplimientoGeneral) }}>
                          {a.cumplimientoGeneral.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-(--admin-text3) text-[11px]">—</span>
                      )}
                    </td>
                  )}
                  {visible.rechazos && (
                    <td
                      className="text-center whitespace-nowrap"
                      title={
                        extractRechazos(a.recomendaciones ?? '').length > 0
                          ? extractRechazos(a.recomendaciones ?? '')
                              .map((r) => `[${r.fecha}]: ${r.contenido}`)
                              .join('\n')
                          : 'Sin motivos de rechazo'
                      }
                    >
                      <span className="admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red) border-transparent">{rechazos}</span>
                    </td>
                  )}
                  {visible.evidencias && (
                    <td className="text-center">
                      <span className="admin-chip text-[10px]">{a.evidencias?.length ?? 0}</span>
                    </td>
                  )}
                  {visible.audios && (
                    <td className="text-center">
                      <span className="admin-chip text-[10px]">{a.audios?.length ?? 0}</span>
                    </td>
                  )}

                  {/* Dynamic NTC columns */}
                  {ntcCols.map((col) => {
                    if (!visible[col.key]) return null;
                    const val = ntcMap.get(col.key);
                    const isPercent = col.key.match(/^ntc_\d+$/) !== null;
                    return (
                      <td key={col.key} className="text-center whitespace-nowrap">
                        {val !== undefined ? (
                          isPercent ? (
                            <span className="font-bold text-[12px]" style={{ color: pColor(val) }}>
                              {val.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="font-medium text-[12px] text-(--admin-text2)">{val}</span>
                          )
                        ) : (
                          <span className="text-(--admin-text3) text-[11px]">—</span>
                        )}
                      </td>
                    );
                  })}

                  <td className="text-right">
                    <ExternalLink className="w-3.5 h-3.5 text-(--admin-text3) opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
